import { MsalProvider } from '@azure/msal-react';
import { CdSpinner } from '@ui';
import { QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense, useEffect, type ReactNode } from 'react';
import { Provider as ReduxProvider } from 'react-redux';
import { store } from '@/app/store/store';
import { msalInstance } from '@/modules/account/auth/msalConfig';
import { authEvents } from '@/shared/auth/tokenProvider';
import { ToastHost } from '@/app/toast/ToastHost';
import { queryClient } from './queryClient';

// Devtools are code-split and only loaded in development.
const ReactQueryDevtools = import.meta.env.DEV
  ? lazy(() =>
      import('@tanstack/react-query-devtools').then((m) => ({ default: m.ReactQueryDevtools })),
    )
  : () => null;

/** Drops the local session without a redirect. RequireSession then sends the user to sign in. */
const endSession = async () => {
  queryClient.clear();
  await msalInstance.clearCache();
  // Fires ACTIVE_ACCOUNT_CHANGED after the accounts are gone, so MsalProvider re-reads them.
  msalInstance.setActiveAccount(null);
};

/** Composition root for app-wide providers. Order: auth → state → server cache → i18n suspense. */
export const AppProviders = ({ children }: { children: ReactNode }) => {
  // session-expired: still 401 after a forced token refresh.
  // interaction-required: MSAL can't renew silently (expired refresh token, MFA, consent).
  useEffect(() => {
    const onEnd = () => void endSession();
    const offExpired = authEvents.on('session-expired', onEnd);
    const offInteraction = authEvents.on('interaction-required', onEnd);
    return () => {
      offExpired();
      offInteraction();
    };
  }, []);

  return (
    <MsalProvider instance={msalInstance}>
      <ReduxProvider store={store}>
        <QueryClientProvider client={queryClient}>
          {/* Suspense covers i18n namespace loading and lazy route chunks. */}
          <Suspense fallback={<CdSpinner variant="overlay" />}>{children}</Suspense>
          {/* Global toasts (raised via the toast slice); outside the router so every layout gets them. */}
          <Suspense fallback={null}>
            <ToastHost />
          </Suspense>
          <Suspense fallback={null}>
            <ReactQueryDevtools buttonPosition="bottom-left" />
          </Suspense>
        </QueryClientProvider>
      </ReduxProvider>
    </MsalProvider>
  );
};
